import React, { useState, Component } from 'react';
import { Card } from '../ui/Card';
import { User, Mail, Lock, Bell, Layout, Globe, Shield, HelpCircle, CreditCard, Users, Edit, Save, X, Eye, EyeOff, Check, AlertTriangle, RefreshCw, Trash, CreditCard as CreditCardIcon, Calendar, Download, FileText, Building, UserPlus, Briefcase, Phone, MessageSquare, Video, BookOpen, ExternalLink, Clock, DollarSign, BarChart, Filter, Search, Plus, UserCheck, Key, LogOut, Settings, Languages } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
export const SettingsPage = () => {
  const {
    language,
    toggleLanguage
  } = useLanguage();
  const isRTL = language === 'ar';
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: 'Admin User',
    email: 'admin@example.com',
    phone: '+1 (555) 123-4567',
    role: 'Administrator',
    bio: 'Platform administrator responsible for managing users, courses, and system settings.',
    password: '••••••••••••',
    newPassword: '',
    confirmPassword: '',
    notifications: {
      email: true,
      browser: true,
      mobile: false,
      userRegistration: true,
      courseEnrollment: true,
      messageReceived: true,
      systemUpdates: true
    },
    appearance: {
      theme: 'system',
      compactMode: false,
      animationsEnabled: true
    },
    language: language,
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    firstDayOfWeek: 'sunday',
    numberFormat: 'en-US',
    privacy: {
      profileVisibility: 'public',
      showOnlineStatus: true,
      allowMessages: 'all',
      shareActivity: true,
      dataUsage: true
    },
    payment: {
      defaultMethod: 'card',
      currency: 'USD',
      autoRenew: true
    },
    teamRole: 'owner'
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (category: string, setting: string) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: !(prev[category as keyof typeof prev] as any)[setting]
      }
    }));
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      appearance: {
        ...prev.appearance,
        [name]: value
      }
    }));
  };
  const handlePrivacyChange = (setting: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [setting]: value
      }
    }));
  };
  const handlePaymentChange = (setting: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        [setting]: value
      }
    }));
  };
  const handleSave = () => {
    // In a real app, you would save the data to the backend
    console.log('Saving data:', formData);
    // If language was changed, apply it
    if (formData.language !== language) {
      toggleLanguage();
    }
    setIsEditing(false);
  };
  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      ...formData,
      language: language // Ensure language matches the current UI language
    });
    setIsEditing(false);
  };
  const tabs = [{
    id: 'profile',
    label: isRTL ? 'الملف الشخصي' : 'Profile',
    icon: <User className="w-5 h-5" data-id="element-774" />
  }, {
    id: 'security',
    label: isRTL ? 'الأمان' : 'Security',
    icon: <Lock className="w-5 h-5" data-id="element-775" />
  }, {
    id: 'notifications',
    label: isRTL ? 'الإشعارات' : 'Notifications',
    icon: <Bell className="w-5 h-5" data-id="element-776" />
  }, {
    id: 'appearance',
    label: isRTL ? 'المظهر' : 'Appearance',
    icon: <Layout className="w-5 h-5" data-id="element-777" />
  }, {
    id: 'language',
    label: isRTL ? 'اللغة والمنطقة' : 'Language & Region',
    icon: <Globe className="w-5 h-5" data-id="element-778" />
  }, {
    id: 'privacy',
    label: isRTL ? 'الخصوصية' : 'Privacy',
    icon: <Shield className="w-5 h-5" data-id="element-779" />
  }, {
    id: 'billing',
    label: isRTL ? 'الفواتير' : 'Billing',
    icon: <CreditCard className="w-5 h-5" data-id="element-780" />
  }, {
    id: 'team',
    label: isRTL ? 'أعضاء الفريق' : 'Team Members',
    icon: <Users className="w-5 h-5" data-id="element-781" />
  }, {
    id: 'help',
    label: isRTL ? 'المساعدة والدعم' : 'Help & Support',
    icon: <HelpCircle className="w-5 h-5" data-id="element-782" />
  }];
  return <div className="grid grid-cols-1 md:grid-cols-4 gap-6" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-783">
      {/* Sidebar */}
      <div className="md:col-span-1" data-id="element-784">
        <Card className="p-0 overflow-hidden" data-id="element-785">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-786">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-787">
              {isRTL ? 'الإعدادات' : 'Settings'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-788">
              {isRTL ? 'إدارة إعدادات حسابك' : 'Manage your account settings'}
            </p>
          </div>
          <nav className="p-2" data-id="element-789">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`} data-id="element-790">
                {tab.icon}
                <span data-id="element-791">{tab.label}</span>
              </button>)}
          </nav>
        </Card>
      </div>
      {/* Main Content */}
      <div className="md:col-span-3" data-id="element-792">
        <Card data-id="element-793">
          {/* Profile Settings */}
          {activeTab === 'profile' && <div data-id="element-794">
              <div className="flex items-center justify-between mb-6" data-id="element-795">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-796">
                  {isRTL ? 'معلومات الملف الشخصي' : 'Profile Information'}
                </h2>
                {!isEditing ? <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-797">
                    <Edit className="w-4 h-4" data-id="element-798" />
                    <span data-id="element-799">{isRTL ? 'تعديل الملف الشخصي' : 'Edit Profile'}</span>
                  </button> : <div className="flex items-center gap-2" data-id="element-800">
                    <button onClick={handleCancel} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-801">
                      <X className="w-4 h-4" data-id="element-802" />
                      <span data-id="element-803">{isRTL ? 'إلغاء' : 'Cancel'}</span>
                    </button>
                    <button onClick={handleSave} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors" data-id="element-804">
                      <Save className="w-4 h-4" data-id="element-805" />
                      <span data-id="element-806">{isRTL ? 'حفظ التغييرات' : 'Save Changes'}</span>
                    </button>
                  </div>}
              </div>
              <div className="space-y-6" data-id="element-807">
                <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-808">
                  <div className="w-24 h-24 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 text-2xl font-semibold" data-id="element-809">
                    {formData.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-810">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-811">
                      <div data-id="element-812">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-813">
                          {isRTL ? 'الاسم الكامل' : 'Full Name'}
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} disabled={!isEditing} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400" data-id="element-814" />
                      </div>
                      <div data-id="element-815">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-816">
                          {isRTL ? 'البريد الإلكتروني' : 'Email Address'}
                        </label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} disabled={!isEditing} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400" data-id="element-817" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-818">
                      <div data-id="element-819">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-820">
                          {isRTL ? 'رقم الهاتف' : 'Phone Number'}
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} disabled={!isEditing} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400" data-id="element-821" />
                      </div>
                      <div data-id="element-822">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-823">
                          {isRTL ? 'الدور' : 'Role'}
                        </label>
                        <input type="text" name="role" value={formData.role} onChange={handleInputChange} disabled={true} // Role is always disabled
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400" data-id="element-824" />
                      </div>
                    </div>
                    <div data-id="element-825">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-826">
                        {isRTL ? 'نبذة شخصية' : 'Bio'}
                      </label>
                      <textarea name="bio" value={formData.bio} onChange={handleInputChange} disabled={!isEditing} rows={4} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 resize-none" data-id="element-827"></textarea>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-828">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-829">
                    {isRTL ? 'معلومات الحساب' : 'Account Information'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-830">
                    <div data-id="element-831">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-832">
                        <span className="font-medium" data-id="element-833">
                          {isRTL ? 'تاريخ إنشاء الحساب:' : 'Account Created:'}
                        </span>{' '}
                        {isRTL ? '١٥ يناير ٢٠٢٤' : 'January 15, 2024'}
                      </p>
                    </div>
                    <div data-id="element-834">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-835">
                        <span className="font-medium" data-id="element-836">
                          {isRTL ? 'آخر تسجيل دخول:' : 'Last Login:'}
                        </span>{' '}
                        {isRTL ? 'اليوم الساعة ٩:٤٢ صباحًا' : 'Today at 9:42 AM'}
                      </p>
                    </div>
                    <div data-id="element-837">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-838">
                        <span className="font-medium" data-id="element-839">
                          {isRTL ? 'حالة الحساب:' : 'Account Status:'}
                        </span>{' '}
                        <span className="text-green-600 dark:text-green-400" data-id="element-840">
                          {isRTL ? 'نشط' : 'Active'}
                        </span>
                      </p>
                    </div>
                    <div data-id="element-841">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-842">
                        <span className="font-medium" data-id="element-843">
                          {isRTL ? 'المصادقة الثنائية:' : 'Two-Factor Authentication:'}
                        </span>{' '}
                        <span className="text-red-600 dark:text-red-400" data-id="element-844">
                          {isRTL ? 'معطلة' : 'Disabled'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {/* Security Settings */}
          {activeTab === 'security' && <div data-id="element-845">
              <div className="flex items-center justify-between mb-6" data-id="element-846">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-847">
                  {isRTL ? 'إعدادات الأمان' : 'Security Settings'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-848">
                <div data-id="element-849">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-850">
                    {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-851">
                    <div data-id="element-852">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-853">
                        {isRTL ? 'كلمة المرور الحالية' : 'Current Password'}
                      </label>
                      <div className="relative" data-id="element-854">
                        <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-10" data-id="element-855" />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => setShowPassword(!showPassword)} data-id="element-856">
                          {showPassword ? <EyeOff className="w-4 h-4" data-id="element-857" /> : <Eye className="w-4 h-4" data-id="element-858" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-859">
                    <div data-id="element-860">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-861">
                        {isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
                      </label>
                      <input type="password" name="newPassword" value={formData.newPassword} onChange={handleInputChange} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-862" />
                    </div>
                    <div data-id="element-863">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-864">
                        {isRTL ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password'}
                      </label>
                      <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-865" />
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-866">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-867">
                      {isRTL ? 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل وتتضمن أحرفًا كبيرة وصغيرة ورقمًا وحرفًا خاصًا.' : 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.'}
                    </p>
                  </div>
                  <div className="mt-4" data-id="element-868">
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-869">
                      {isRTL ? 'تحديث كلمة المرور' : 'Update Password'}
                    </button>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-870">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-871">
                    {isRTL ? 'المصادقة الثنائية' : 'Two-Factor Authentication'}
                  </h3>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4 mb-4" data-id="element-872">
                    <div className="flex items-start gap-3" data-id="element-873">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" data-id="element-874" />
                      <div data-id="element-875">
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-876">
                          {isRTL ? 'المصادقة الثنائية غير مفعلة' : 'Two-factor authentication is not enabled'}
                        </h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1" data-id="element-877">
                          {isRTL ? 'أضف طبقة إضافية من الأمان إلى حسابك عن طريق تمكين المصادقة الثنائية.' : 'Add an extra layer of security to your account by enabling two-factor authentication.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-878">
                    {isRTL ? 'تفعيل المصادقة الثنائية' : 'Enable Two-Factor Authentication'}
                  </button>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-879">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-880">
                    {isRTL ? 'الجلسات النشطة' : 'Active Sessions'}
                  </h3>
                  <div className="space-y-4" data-id="element-881">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-882">
                      <div className="flex items-center justify-between" data-id="element-883">
                        <div data-id="element-884">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-885">
                            {isRTL ? 'الجلسة الحالية' : 'Current Session'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-886">
                            {isRTL ? 'كروم على ويندوز • الآي بي: 192.168.1.1 • نشط الآن' : 'Chrome on Windows • IP: 192.168.1.1 • Active now'}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs rounded-full" data-id="element-887">
                          {isRTL ? 'حالي' : 'Current'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-888">
                      <div className="flex items-center justify-between" data-id="element-889">
                        <div data-id="element-890">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-891">
                            {isRTL ? 'سفاري على نظام ماك' : 'Safari on MacOS'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-892">
                            {isRTL ? 'الآي بي: 192.168.1.2 • آخر نشاط: منذ يومين' : 'IP: 192.168.1.2 • Last active: 2 days ago'}
                          </p>
                        </div>
                        <button className="text-red-600 dark:text-red-400 text-sm hover:underline" data-id="element-893">
                          {isRTL ? 'إلغاء' : 'Revoke'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button className="mt-4 flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-894">
                    <RefreshCw className="w-4 h-4" data-id="element-895" />
                    <span data-id="element-896">{isRTL ? 'تحديث الجلسات' : 'Refresh Sessions'}</span>
                  </button>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-897">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-898">
                    {isRTL ? 'منطقة الخطر' : 'Danger Zone'}
                  </h3>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg" data-id="element-899">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2" data-id="element-900">
                      {isRTL ? 'حذف الحساب' : 'Delete Account'}
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-400 mb-4" data-id="element-901">
                      {isRTL ? 'بمجرد حذف حسابك، لا يمكن التراجع عن ذلك. يرجى التأكد.' : 'Once you delete your account, there is no going back. Please be certain.'}
                    </p>
                    <button className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors" data-id="element-902">
                      <Trash className="w-4 h-4" data-id="element-903" />
                      <span data-id="element-904">{isRTL ? 'حذف الحساب' : 'Delete Account'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Notifications Settings */}
          {activeTab === 'notifications' && <div data-id="element-905">
              <div className="flex items-center justify-between mb-6" data-id="element-906">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-907">
                  {isRTL ? 'تفضيلات الإشعارات' : 'Notification Preferences'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-908">
                <div data-id="element-909">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-910">
                    {isRTL ? 'قنوات الإشعارات' : 'Notification Channels'}
                  </h3>
                  <div className="space-y-4" data-id="element-911">
                    <div className="flex items-center justify-between" data-id="element-912">
                      <div className="flex items-center gap-3" data-id="element-913">
                        <Mail className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-914" />
                        <div data-id="element-915">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-916">
                            {isRTL ? 'إشعارات البريد الإلكتروني' : 'Email Notifications'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-917">
                            {isRTL ? 'تلقي إشعارات البريد الإلكتروني حول التحديثات المهمة' : 'Receive email notifications about important updates'}
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-918">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.email} onChange={() => handleCheckboxChange('notifications', 'email')} data-id="element-919" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-920"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-921">
                      <div className="flex items-center gap-3" data-id="element-922">
                        <Bell className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-923" />
                        <div data-id="element-924">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-925">
                            {isRTL ? 'إشعارات المتصفح' : 'Browser Notifications'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-926">
                            {isRTL ? 'عرض إشعارات سطح المكتب في متصفحك' : 'Show desktop notifications in your browser'}
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-927">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.browser} onChange={() => handleCheckboxChange('notifications', 'browser')} data-id="element-928" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-929"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-930">
                      <div className="flex items-center gap-3" data-id="element-931">
                        <Globe className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-932" />
                        <div data-id="element-933">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-934">
                            {isRTL ? 'إشعارات الجوال' : 'Mobile Push Notifications'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-935">
                            {isRTL ? 'تلقي إشعارات على جهازك المحمول' : 'Receive push notifications on your mobile device'}
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-936">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.mobile} onChange={() => handleCheckboxChange('notifications', 'mobile')} data-id="element-937" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-938"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-939">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-940">
                    {isRTL ? 'أنواع الإشعارات' : 'Notification Types'}
                  </h3>
                  <div className="space-y-4" data-id="element-941">
                    <div className="flex items-center justify-between" data-id="element-942">
                      <div data-id="element-943">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-944">
                          {isRTL ? 'تسجيلات المستخدمين الجدد' : 'New User Registrations'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-945">
                          {isRTL ? 'إشعار عند تسجيل مستخدم جديد على المنصة' : 'Notify when a new user registers on the platform'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-946">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.userRegistration} onChange={() => handleCheckboxChange('notifications', 'userRegistration')} data-id="element-947" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-948"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-949">
                      <div data-id="element-950">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-951">
                          {isRTL ? 'التسجيل في الدورات' : 'Course Enrollments'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-952">
                          {isRTL ? 'إشعار عند تسجيل مستخدم في دورة' : 'Notify when a user enrolls in a course'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-953">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.courseEnrollment} onChange={() => handleCheckboxChange('notifications', 'courseEnrollment')} data-id="element-954" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-955"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-956">
                      <div data-id="element-957">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-958">
                          {isRTL ? 'رسائل جديدة' : 'New Messages'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-959">
                          {isRTL ? 'إشعار عند استلام رسالة جديدة' : 'Notify when you receive a new message'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-960">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.messageReceived} onChange={() => handleCheckboxChange('notifications', 'messageReceived')} data-id="element-961" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-962"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-963">
                      <div data-id="element-964">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-965">
                          {isRTL ? 'تحديثات النظام' : 'System Updates'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-966">
                          {isRTL ? 'إشعار حول تحديثات النظام والصيانة' : 'Notify about system updates and maintenance'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-967">
                        <input type="checkbox" className="sr-only peer" checked={formData.notifications.systemUpdates} onChange={() => handleCheckboxChange('notifications', 'systemUpdates')} data-id="element-968" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-969"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-970">
                  <div className="flex justify-end" data-id="element-971">
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-972">
                      {isRTL ? 'حفظ إعدادات الإشعارات' : 'Save Notification Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Appearance Settings */}
          {activeTab === 'appearance' && <div data-id="element-973">
              <div className="flex items-center justify-between mb-6" data-id="element-974">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-975">
                  {isRTL ? 'إعدادات المظهر' : 'Appearance Settings'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-976">
                <div data-id="element-977">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-978">
                    {isRTL ? 'السمة' : 'Theme'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-979">
                    <div className={`border rounded-lg p-4 cursor-pointer transition-all ${formData.appearance.theme === 'light' ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-980">
                      <div className="bg-white border border-gray-200 rounded-md h-24 mb-3 flex items-center justify-center" data-id="element-981">
                        <Sun className="w-8 h-8 text-gray-400" data-id="element-982" />
                      </div>
                      <div className="flex items-center justify-between" data-id="element-983">
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-984">
                          {isRTL ? 'فاتح' : 'Light'}
                        </span>
                        {formData.appearance.theme === 'light' && <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" data-id="element-985" />}
                      </div>
                    </div>
                    <div className={`border rounded-lg p-4 cursor-pointer transition-all ${formData.appearance.theme === 'dark' ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-986">
                      <div className="bg-gray-900 border border-gray-700 rounded-md h-24 mb-3 flex items-center justify-center" data-id="element-987">
                        <Moon className="w-8 h-8 text-gray-400" data-id="element-988" />
                      </div>
                      <div className="flex items-center justify-between" data-id="element-989">
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-990">
                          {isRTL ? 'داكن' : 'Dark'}
                        </span>
                        {formData.appearance.theme === 'dark' && <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" data-id="element-991" />}
                      </div>
                    </div>
                    <div className={`border rounded-lg p-4 cursor-pointer transition-all ${formData.appearance.theme === 'system' ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-992">
                      <div className="bg-gradient-to-r from-white to-gray-900 border border-gray-200 rounded-md h-24 mb-3 flex items-center justify-center" data-id="element-993">
                        <Computer className="w-8 h-8 text-gray-400" data-id="element-994" />
                      </div>
                      <div className="flex items-center justify-between" data-id="element-995">
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-996">
                          {isRTL ? 'النظام' : 'System'}
                        </span>
                        {formData.appearance.theme === 'system' && <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" data-id="element-997" />}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-998">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-999">
                    {isRTL ? 'خيارات العرض' : 'Display Options'}
                  </h3>
                  <div className="space-y-4" data-id="element-1000">
                    <div className="flex items-center justify-between" data-id="element-1001">
                      <div data-id="element-1002">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1003">
                          {isRTL ? 'الوضع المضغوط' : 'Compact Mode'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1004">
                          {isRTL ? 'تقليل المسافات والحشوات في جميع أنحاء الواجهة' : 'Reduce spacing and padding throughout the interface'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1005">
                        <input type="checkbox" className="sr-only peer" checked={formData.appearance.compactMode} onChange={() => handleCheckboxChange('appearance', 'compactMode')} data-id="element-1006" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1007"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1008">
                      <div data-id="element-1009">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1010">
                          {isRTL ? 'تمكين الرسوم المتحركة' : 'Enable Animations'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1011">
                          {isRTL ? 'إظهار الرسوم المتحركة والانتقالات في جميع أنحاء الواجهة' : 'Show animations and transitions throughout the interface'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1012">
                        <input type="checkbox" className="sr-only peer" checked={formData.appearance.animationsEnabled} onChange={() => handleCheckboxChange('appearance', 'animationsEnabled')} data-id="element-1013" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1014"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1015">
                  <div className="flex justify-end" data-id="element-1016">
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1017">
                      {isRTL ? 'حفظ إعدادات المظهر' : 'Save Appearance Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Language & Region Settings */}
          {activeTab === 'language' && <div data-id="element-1018">
              <div className="flex items-center justify-between mb-6" data-id="element-1019">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1020">
                  {isRTL ? 'إعدادات اللغة والمنطقة' : 'Language & Region Settings'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-1021">
                <div data-id="element-1022">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1023">
                    {isRTL ? 'لغة الواجهة' : 'Interface Language'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1024">
                    <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-4 ${formData.language === 'en' ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => setFormData({
                  ...formData,
                  language: 'en'
                })} data-id="element-1025">
                      <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30" data-id="element-1026">
                        <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1027" />
                      </div>
                      <div data-id="element-1028">
                        <div className="flex items-center gap-2" data-id="element-1029">
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1030">
                            English
                          </span>
                          {formData.language === 'en' && <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" data-id="element-1031" />}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1032">
                          {isRTL ? 'عرض الواجهة باللغة الإنجليزية' : 'Display the interface in English'}
                        </p>
                      </div>
                    </div>
                    <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-4 ${formData.language === 'ar' ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => setFormData({
                  ...formData,
                  language: 'ar'
                })} data-id="element-1033">
                      <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30" data-id="element-1034">
                        <Globe className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1035" />
                      </div>
                      <div data-id="element-1036">
                        <div className="flex items-center gap-2" data-id="element-1037">
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1038">
                            العربية (Arabic)
                          </span>
                          {formData.language === 'ar' && <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" data-id="element-1039" />}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right" dir="rtl" data-id="element-1040">
                          عرض الواجهة باللغة العربية
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1041">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1042">
                    {isRTL ? 'المنطقة الزمنية' : 'Time Zone'}
                  </h3>
                  <div className="mb-4" data-id="element-1043">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1044">
                      {isRTL ? 'اختر المنطقة الزمنية' : 'Select Time Zone'}
                    </label>
                    <select value={formData.timezone} onChange={e => setFormData({
                  ...formData,
                  timezone: e.target.value
                })} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1045">
                      <option value="America/New_York" data-id="element-1046">
                        {isRTL ? 'التوقيت الشرقي (الولايات المتحدة وكندا)' : 'Eastern Time (US & Canada)'}
                      </option>
                      <option value="America/Chicago" data-id="element-1047">
                        {isRTL ? 'التوقيت المركزي (الولايات المتحدة وكندا)' : 'Central Time (US & Canada)'}
                      </option>
                      <option value="America/Denver" data-id="element-1048">
                        {isRTL ? 'توقيت الجبال (الولايات المتحدة وكندا)' : 'Mountain Time (US & Canada)'}
                      </option>
                      <option value="America/Los_Angeles" data-id="element-1049">
                        {isRTL ? 'توقيت المحيط الهادئ (الولايات المتحدة وكندا)' : 'Pacific Time (US & Canada)'}
                      </option>
                      <option value="Europe/London" data-id="element-1050">
                        {isRTL ? 'لندن، المملكة المتحدة' : 'London, United Kingdom'}
                      </option>
                      <option value="Europe/Paris" data-id="element-1051">
                        {isRTL ? 'باريس، فرنسا' : 'Paris, France'}
                      </option>
                      <option value="Asia/Dubai" data-id="element-1052">
                        {isRTL ? 'دبي، الإمارات العربية المتحدة' : 'Dubai, UAE'}
                      </option>
                      <option value="Asia/Riyadh" data-id="element-1053">
                        {isRTL ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia'}
                      </option>
                      <option value="Asia/Amman" data-id="element-1054">
                        {isRTL ? 'عمان، الأردن' : 'Amman, Jordan'}
                      </option>
                      <option value="Asia/Tokyo" data-id="element-1055">
                        {isRTL ? 'طوكيو، اليابان' : 'Tokyo, Japan'}
                      </option>
                      <option value="Australia/Sydney" data-id="element-1056">
                        {isRTL ? 'سيدني، أستراليا' : 'Sydney, Australia'}
                      </option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1057">
                      {isRTL ? 'الوقت المحلي الحالي:' : 'Current local time:'}{' '}
                      {new Date().toLocaleTimeString(formData.language === 'ar' ? 'ar-SA' : 'en-US', {
                    timeZone: formData.timezone
                  })}
                    </p>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1058">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1059">
                    {isRTL ? 'تنسيق التاريخ والوقت' : 'Date & Time Format'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1060">
                    <div data-id="element-1061">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1062">
                        {isRTL ? 'تنسيق التاريخ' : 'Date Format'}
                      </label>
                      <select value={formData.dateFormat || 'MM/DD/YYYY'} onChange={e => setFormData({
                    ...formData,
                    dateFormat: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1063">
                        <option value="MM/DD/YYYY" data-id="element-1064">MM/DD/YYYY</option>
                        <option value="DD/MM/YYYY" data-id="element-1065">DD/MM/YYYY</option>
                        <option value="YYYY-MM-DD" data-id="element-1066">YYYY-MM-DD</option>
                        <option value="YYYY/MM/DD" data-id="element-1067">YYYY/MM/DD</option>
                        <option value="DD-MMM-YYYY" data-id="element-1068">DD-MMM-YYYY</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1069">
                        {isRTL ? 'مثال:' : 'Example:'}{' '}
                        {formatDateExample(new Date(), formData.dateFormat || 'MM/DD/YYYY')}
                      </p>
                    </div>
                    <div data-id="element-1070">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1071">
                        {isRTL ? 'تنسيق الوقت' : 'Time Format'}
                      </label>
                      <select value={formData.timeFormat || '12'} onChange={e => setFormData({
                    ...formData,
                    timeFormat: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1072">
                        <option value="12" data-id="element-1073">
                          {isRTL ? '12 ساعة (صباحًا/مساءً)' : '12-hour (AM/PM)'}
                        </option>
                        <option value="24" data-id="element-1074">
                          {isRTL ? '24 ساعة' : '24-hour'}
                        </option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1075">
                        {isRTL ? 'مثال:' : 'Example:'}{' '}
                        {formatTimeExample(new Date(), formData.timeFormat || '12')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1076">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1077">
                    {isRTL ? 'إعدادات المنطقة' : 'Locale Settings'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1078">
                    <div data-id="element-1079">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1080">
                        {isRTL ? 'اليوم الأول من الأسبوع' : 'First Day of Week'}
                      </label>
                      <select value={formData.firstDayOfWeek || 'sunday'} onChange={e => setFormData({
                    ...formData,
                    firstDayOfWeek: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1081">
                        <option value="sunday" data-id="element-1082">
                          {isRTL ? 'الأحد' : 'Sunday'}
                        </option>
                        <option value="monday" data-id="element-1083">
                          {isRTL ? 'الاثنين' : 'Monday'}
                        </option>
                        <option value="saturday" data-id="element-1084">
                          {isRTL ? 'السبت' : 'Saturday'}
                        </option>
                      </select>
                    </div>
                    <div data-id="element-1085">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1086">
                        {isRTL ? 'تنسيق الأرقام' : 'Number Format'}
                      </label>
                      <select value={formData.numberFormat || 'en-US'} onChange={e => setFormData({
                    ...formData,
                    numberFormat: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1087">
                        <option value="en-US" data-id="element-1088">1,234.56 (US)</option>
                        <option value="en-GB" data-id="element-1089">1,234.56 (UK)</option>
                        <option value="de-DE" data-id="element-1090">1.234,56 (German)</option>
                        <option value="fr-FR" data-id="element-1091">1 234,56 (French)</option>
                        <option value="ar-SA" data-id="element-1092">١٬٢٣٤٫٥٦ (Arabic)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1093">
                        {isRTL ? 'مثال:' : 'Example:'}{' '}
                        {formatNumberExample(1234.56, formData.numberFormat || 'en-US')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1094">
                  <div className="flex justify-end gap-2" data-id="element-1095">
                    <button onClick={handleCancel} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1096">
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2" data-id="element-1097">
                      <Save className="w-4 h-4" data-id="element-1098" />
                      {isRTL ? 'حفظ الإعدادات' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Privacy Settings */}
          {activeTab === 'privacy' && <div data-id="element-1099">
              <div className="flex items-center justify-between mb-6" data-id="element-1100">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1101">
                  {isRTL ? 'إعدادات الخصوصية' : 'Privacy Settings'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-1102">
                <div data-id="element-1103">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1104">
                    {isRTL ? 'إعدادات الملف الشخصي' : 'Profile Settings'}
                  </h3>
                  <div className="space-y-6" data-id="element-1105">
                    <div data-id="element-1106">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1107">
                        {isRTL ? 'رؤية الملف الشخصي' : 'Profile Visibility'}
                      </label>
                      <select value={formData.privacy.profileVisibility} onChange={e => handlePrivacyChange('profileVisibility', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1108">
                        <option value="public" data-id="element-1109">
                          {isRTL ? 'عام - يمكن للجميع رؤية ملفك الشخصي' : 'Public - Everyone can see your profile'}
                        </option>
                        <option value="registered" data-id="element-1110">
                          {isRTL ? 'مسجل - يمكن للمستخدمين المسجلين فقط رؤية ملفك الشخصي' : 'Registered - Only registered users can see your profile'}
                        </option>
                        <option value="connections" data-id="element-1111">
                          {isRTL ? 'اتصالات - يمكن لاتصالاتك فقط رؤية ملفك الشخصي' : 'Connections - Only your connections can see your profile'}
                        </option>
                        <option value="private" data-id="element-1112">
                          {isRTL ? 'خاص - لا يمكن لأحد رؤية ملفك الشخصي' : 'Private - No one can see your profile'}
                        </option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1113">
                        {isRTL ? 'يحدد من يمكنه رؤية معلومات ملفك الشخصي' : 'Determines who can see your profile information'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1114">
                      <div data-id="element-1115">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1116">
                          {isRTL ? 'إظهار حالة الاتصال' : 'Show Online Status'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1117">
                          {isRTL ? 'إظهار متى تكون متصلاً بالمنصة' : 'Show when you are online on the platform'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1118">
                        <input type="checkbox" className="sr-only peer" checked={formData.privacy.showOnlineStatus} onChange={() => handlePrivacyChange('showOnlineStatus', !formData.privacy.showOnlineStatus)} data-id="element-1119" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1120"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1121">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1122">
                    {isRTL ? 'إعدادات الاتصال' : 'Communication Settings'}
                  </h3>
                  <div className="space-y-6" data-id="element-1123">
                    <div data-id="element-1124">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1125">
                        {isRTL ? 'السماح بالرسائل من' : 'Allow Messages From'}
                      </label>
                      <select value={formData.privacy.allowMessages} onChange={e => handlePrivacyChange('allowMessages', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1126">
                        <option value="all" data-id="element-1127">
                          {isRTL ? 'الجميع' : 'Everyone'}
                        </option>
                        <option value="connections" data-id="element-1128">
                          {isRTL ? 'اتصالاتي فقط' : 'My Connections Only'}
                        </option>
                        <option value="none" data-id="element-1129">
                          {isRTL ? 'لا أحد' : 'No One'}
                        </option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1130">
                        {isRTL ? 'يحدد من يمكنه إرسال رسائل إليك' : 'Determines who can send you messages'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1131">
                      <div data-id="element-1132">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1133">
                          {isRTL ? 'مشاركة نشاطك' : 'Share Your Activity'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1134">
                          {isRTL ? 'مشاركة نشاطك التعليمي مع اتصالاتك' : 'Share your learning activity with your connections'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1135">
                        <input type="checkbox" className="sr-only peer" checked={formData.privacy.shareActivity} onChange={() => handlePrivacyChange('shareActivity', !formData.privacy.shareActivity)} data-id="element-1136" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1137"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1138">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1139">
                    {isRTL ? 'استخدام البيانات' : 'Data Usage'}
                  </h3>
                  <div className="space-y-6" data-id="element-1140">
                    <div className="flex items-center justify-between" data-id="element-1141">
                      <div data-id="element-1142">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1143">
                          {isRTL ? 'تحسين المنصة' : 'Platform Improvement'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1144">
                          {isRTL ? 'السماح باستخدام بياناتك لتحسين تجربة المنصة' : 'Allow your data to be used to improve platform experience'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1145">
                        <input type="checkbox" className="sr-only peer" checked={formData.privacy.dataUsage} onChange={() => handlePrivacyChange('dataUsage', !formData.privacy.dataUsage)} data-id="element-1146" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1147"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1148">
                  <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-4 mb-6" data-id="element-1149">
                    <div className="flex items-start gap-3" data-id="element-1150">
                      <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" data-id="element-1151" />
                      <div data-id="element-1152">
                        <h4 className="text-sm font-medium text-primary-800 dark:text-primary-300" data-id="element-1153">
                          {isRTL ? 'بياناتك محمية' : 'Your Data is Protected'}
                        </h4>
                        <p className="text-sm text-primary-700 dark:text-primary-400 mt-1" data-id="element-1154">
                          {isRTL ? 'نحن نأخذ خصوصيتك على محمل الجد. يمكنك الاطلاع على سياسة الخصوصية الكاملة الخاصة بنا للحصول على مزيد من المعلومات.' : 'We take your privacy seriously. You can view our full privacy policy for more information.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-1155">
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1156">
                      {isRTL ? 'حفظ إعدادات الخصوصية' : 'Save Privacy Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Billing Settings */}
          {activeTab === 'billing' && <div data-id="element-1157">
              <div className="flex items-center justify-between mb-6" data-id="element-1158">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1159">
                  {isRTL ? 'إعدادات الفواتير' : 'Billing Settings'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-1160">
                <div data-id="element-1161">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1162">
                    {isRTL ? 'خطة الاشتراك الحالية' : 'Current Subscription Plan'}
                  </h3>
                  <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/20 rounded-lg p-6 border border-primary-200 dark:border-primary-800/30" data-id="element-1163">
                    <div className="flex flex-col md:flex-row justify-between gap-4" data-id="element-1164">
                      <div data-id="element-1165">
                        <div className="flex items-center gap-2" data-id="element-1166">
                          <Badge variant="success" className="text-xs px-2 py-0.5 rounded" data-id="element-1167">
                            {isRTL ? 'نشط' : 'Active'}
                          </Badge>
                          <h4 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1168">
                            {isRTL ? 'الخطة الاحترافية' : 'Professional Plan'}
                          </h4>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-1169">
                          {isRTL ? 'تجدد في 15 يونيو 2025' : 'Renews on June 15, 2025'}
                        </div>
                        <div className="mt-4 text-sm text-gray-600 dark:text-gray-300" data-id="element-1170">
                          <p data-id="element-1171">{isRTL ? 'يشمل:' : 'Includes:'}</p>
                          <ul className="mt-2 space-y-1" data-id="element-1172">
                            <li className="flex items-center gap-2" data-id="element-1173">
                              <Check className="w-4 h-4 text-green-500" data-id="element-1174" />
                              {isRTL ? 'وصول غير محدود إلى جميع الدورات' : 'Unlimited access to all courses'}
                            </li>
                            <li className="flex items-center gap-2" data-id="element-1175">
                              <Check className="w-4 h-4 text-green-500" data-id="element-1176" />
                              {isRTL ? 'دعم مخصص' : 'Dedicated support'}
                            </li>
                            <li className="flex items-center gap-2" data-id="element-1177">
                              <Check className="w-4 h-4 text-green-500" data-id="element-1178" />
                              {isRTL ? 'تحليلات متقدمة' : 'Advanced analytics'}
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="text-center md:text-right" data-id="element-1179">
                        <div className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1180">
                          $49.99
                          <span className="text-sm font-normal text-gray-500 dark:text-gray-400" data-id="element-1181">
                            /{isRTL ? 'شهر' : 'month'}
                          </span>
                        </div>
                        <div className="mt-4 space-y-2" data-id="element-1182">
                          <button className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1183">
                            {isRTL ? 'ترقية الخطة' : 'Upgrade Plan'}
                          </button>
                          <button className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1184">
                            {isRTL ? 'إلغاء الاشتراك' : 'Cancel Subscription'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1185">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1186">
                    {isRTL ? 'طرق الدفع' : 'Payment Methods'}
                  </h3>
                  <div className="space-y-4" data-id="element-1187">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1188">
                      <div className="flex items-center justify-between" data-id="element-1189">
                        <div className="flex items-center gap-3" data-id="element-1190">
                          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30" data-id="element-1191">
                            <CreditCardIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1192" />
                          </div>
                          <div data-id="element-1193">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1194">
                              {isRTL ? 'فيزا' : 'Visa'} •••• 4242
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1195">
                              {isRTL ? 'تنتهي 05/2025' : 'Expires 05/2025'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1196">
                          <Badge variant="primary" size="sm" data-id="element-1197">
                            {isRTL ? 'افتراضي' : 'Default'}
                          </Badge>
                          <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-1198">
                            <Edit className="w-4 h-4" data-id="element-1199" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1200">
                      <div className="flex items-center justify-between" data-id="element-1201">
                        <div className="flex items-center gap-3" data-id="element-1202">
                          <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/30" data-id="element-1203">
                            <CreditCardIcon className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-1204" />
                          </div>
                          <div data-id="element-1205">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1206">
                              {isRTL ? 'ماستر كارد' : 'Mastercard'} •••• 5678
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1207">
                              {isRTL ? 'تنتهي 09/2026' : 'Expires 09/2026'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1208">
                          <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-1209">
                            <Edit className="w-4 h-4" data-id="element-1210" />
                          </button>
                          <button className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" data-id="element-1211">
                            <Trash className="w-4 h-4" data-id="element-1212" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full justify-center" data-id="element-1213">
                      <Plus className="w-4 h-4" data-id="element-1214" />
                      <span data-id="element-1215">
                        {isRTL ? 'إضافة طريقة دفع جديدة' : 'Add New Payment Method'}
                      </span>
                    </button>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1216">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1217">
                    {isRTL ? 'إعدادات الفوترة' : 'Billing Settings'}
                  </h3>
                  <div className="space-y-6" data-id="element-1218">
                    <div data-id="element-1219">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1220">
                        {isRTL ? 'طريقة الدفع الافتراضية' : 'Default Payment Method'}
                      </label>
                      <select value={formData.payment.defaultMethod} onChange={e => handlePaymentChange('defaultMethod', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1221">
                        <option value="card" data-id="element-1222">
                          {isRTL ? 'فيزا •••• 4242' : 'Visa •••• 4242'}
                        </option>
                        <option value="mastercard" data-id="element-1223">
                          {isRTL ? 'ماستر كارد •••• 5678' : 'Mastercard •••• 5678'}
                        </option>
                      </select>
                    </div>
                    <div data-id="element-1224">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1225">
                        {isRTL ? 'العملة' : 'Currency'}
                      </label>
                      <select value={formData.payment.currency} onChange={e => handlePaymentChange('currency', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1226">
                        <option value="USD" data-id="element-1227">
                          {isRTL ? 'دولار أمريكي (USD)' : 'US Dollar (USD)'}
                        </option>
                        <option value="EUR" data-id="element-1228">
                          {isRTL ? 'يورو (EUR)' : 'Euro (EUR)'}
                        </option>
                        <option value="GBP" data-id="element-1229">
                          {isRTL ? 'جنيه إسترليني (GBP)' : 'British Pound (GBP)'}
                        </option>
                        <option value="JOD" data-id="element-1230">
                          {isRTL ? 'دينار أردني (JOD)' : 'Jordanian Dinar (JOD)'}
                        </option>
                        <option value="SAR" data-id="element-1231">
                          {isRTL ? 'ريال سعودي (SAR)' : 'Saudi Riyal (SAR)'}
                        </option>
                        <option value="AED" data-id="element-1232">
                          {isRTL ? 'درهم إماراتي (AED)' : 'UAE Dirham (AED)'}
                        </option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1233">
                      <div data-id="element-1234">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1235">
                          {isRTL ? 'تجديد تلقائي' : 'Auto-Renew Subscription'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1236">
                          {isRTL ? 'تجديد اشتراكك تلقائيًا عند انتهاء صلاحيته' : 'Automatically renew your subscription when it expires'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1237">
                        <input type="checkbox" className="sr-only peer" checked={formData.payment.autoRenew} onChange={() => handlePaymentChange('autoRenew', !formData.payment.autoRenew)} data-id="element-1238" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1239"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1240">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1241">
                    {isRTL ? 'سجل الفواتير' : 'Billing History'}
                  </h3>
                  <div className="overflow-x-auto" data-id="element-1242">
                    <table className="w-full" data-id="element-1243">
                      <thead data-id="element-1244">
                        <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1245">
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1246">
                            {isRTL ? 'التاريخ' : 'Date'}
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1247">
                            {isRTL ? 'الوصف' : 'Description'}
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1248">
                            {isRTL ? 'المبلغ' : 'Amount'}
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1249">
                            {isRTL ? 'الحالة' : 'Status'}
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1250">
                            {isRTL ? 'الفاتورة' : 'Invoice'}
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1251">
                        <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1252">
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1253">
                            {isRTL ? '١٥ مايو ٢٠٢٥' : 'May 15, 2025'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1254">
                            {isRTL ? 'الخطة الاحترافية - اشتراك شهري' : 'Professional Plan - Monthly Subscription'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1255">
                            $49.99
                          </td>
                          <td className="px-2 py-4" data-id="element-1256">
                            <Badge variant="success" size="sm" data-id="element-1257">
                              {isRTL ? 'مدفوع' : 'Paid'}
                            </Badge>
                          </td>
                          <td className="px-2 py-4" data-id="element-1258">
                            <button className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center gap-1" data-id="element-1259">
                              <Download className="w-4 h-4" data-id="element-1260" />
                              {isRTL ? 'تنزيل' : 'Download'}
                            </button>
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1261">
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1262">
                            {isRTL ? '١٥ أبريل ٢٠٢٥' : 'April 15, 2025'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1263">
                            {isRTL ? 'الخطة الاحترافية - اشتراك شهري' : 'Professional Plan - Monthly Subscription'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1264">
                            $49.99
                          </td>
                          <td className="px-2 py-4" data-id="element-1265">
                            <Badge variant="success" size="sm" data-id="element-1266">
                              {isRTL ? 'مدفوع' : 'Paid'}
                            </Badge>
                          </td>
                          <td className="px-2 py-4" data-id="element-1267">
                            <button className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center gap-1" data-id="element-1268">
                              <Download className="w-4 h-4" data-id="element-1269" />
                              {isRTL ? 'تنزيل' : 'Download'}
                            </button>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1270">
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1271">
                            {isRTL ? '١٥ مارس ٢٠٢٥' : 'March 15, 2025'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1272">
                            {isRTL ? 'الخطة الاحترافية - اشتراك شهري' : 'Professional Plan - Monthly Subscription'}
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-900 dark:text-white" data-id="element-1273">
                            $49.99
                          </td>
                          <td className="px-2 py-4" data-id="element-1274">
                            <Badge variant="success" size="sm" data-id="element-1275">
                              {isRTL ? 'مدفوع' : 'Paid'}
                            </Badge>
                          </td>
                          <td className="px-2 py-4" data-id="element-1276">
                            <button className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center gap-1" data-id="element-1277">
                              <Download className="w-4 h-4" data-id="element-1278" />
                              {isRTL ? 'تنزيل' : 'Download'}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>}
          {/* Team Members Settings */}
          {activeTab === 'team' && <div data-id="element-1279">
              <div className="flex items-center justify-between mb-6" data-id="element-1280">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1281">
                  {isRTL ? 'إدارة أعضاء الفريق' : 'Team Members Management'}
                </h2>
                <button className="flex items-center gap-2 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1282">
                  <UserPlus className="w-4 h-4" data-id="element-1283" />
                  <span data-id="element-1284">{isRTL ? 'دعوة عضو' : 'Invite Member'}</span>
                </button>
              </div>
              <div className="space-y-8" data-id="element-1285">
                <div data-id="element-1286">
                  <div className="flex items-center justify-between mb-4" data-id="element-1287">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1288">
                      {isRTL ? 'أعضاء الفريق' : 'Team Members'}
                    </h3>
                    <div className="relative" data-id="element-1289">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-1290" />
                      <input type="text" placeholder={isRTL ? 'البحث عن أعضاء...' : 'Search members...'} className="pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1291" />
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-1292">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1293">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-1294">
                        <div className="flex items-center gap-3" data-id="element-1295">
                          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 text-lg font-semibold" data-id="element-1296">
                            AU
                          </div>
                          <div data-id="element-1297">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1298">
                              {isRTL ? 'مستخدم المشرف' : 'Admin User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1299">
                              admin@example.com
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-1300">
                          <Badge variant="primary" size="sm" data-id="element-1301">
                            {isRTL ? 'مالك' : 'Owner'}
                          </Badge>
                          <div className="flex items-center gap-2 ms-auto" data-id="element-1302">
                            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-1303">
                              <Edit className="w-4 h-4" data-id="element-1304" />
                            </button>
                            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" disabled data-id="element-1305">
                              <Trash className="w-4 h-4 opacity-50" data-id="element-1306" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1307">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-1308">
                        <div className="flex items-center gap-3" data-id="element-1309">
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-700 dark:text-green-400 text-lg font-semibold" data-id="element-1310">
                            JS
                          </div>
                          <div data-id="element-1311">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1312">
                              {isRTL ? 'جون سميث' : 'John Smith'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1313">
                              john@example.com
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-1314">
                          <select className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" defaultValue="admin" data-id="element-1315">
                            <option value="admin" data-id="element-1316">
                              {isRTL ? 'مشرف' : 'Admin'}
                            </option>
                            <option value="editor" data-id="element-1317">
                              {isRTL ? 'محرر' : 'Editor'}
                            </option>
                            <option value="viewer" data-id="element-1318">
                              {isRTL ? 'مشاهد' : 'Viewer'}
                            </option>
                          </select>
                          <div className="flex items-center gap-2 ms-auto" data-id="element-1319">
                            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-1320">
                              <Edit className="w-4 h-4" data-id="element-1321" />
                            </button>
                            <button className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" data-id="element-1322">
                              <Trash className="w-4 h-4" data-id="element-1323" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1324">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-1325">
                        <div className="flex items-center gap-3" data-id="element-1326">
                          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-700 dark:text-purple-400 text-lg font-semibold" data-id="element-1327">
                            SJ
                          </div>
                          <div data-id="element-1328">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1329">
                              {isRTL ? 'سارة جونسون' : 'Sarah Johnson'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1330">
                              sarah@example.com
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-1331">
                          <select className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" defaultValue="editor" data-id="element-1332">
                            <option value="admin" data-id="element-1333">
                              {isRTL ? 'مشرف' : 'Admin'}
                            </option>
                            <option value="editor" data-id="element-1334">
                              {isRTL ? 'محرر' : 'Editor'}
                            </option>
                            <option value="viewer" data-id="element-1335">
                              {isRTL ? 'مشاهد' : 'Viewer'}
                            </option>
                          </select>
                          <div className="flex items-center gap-2 ms-auto" data-id="element-1336">
                            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-1337">
                              <Edit className="w-4 h-4" data-id="element-1338" />
                            </button>
                            <button className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" data-id="element-1339">
                              <Trash className="w-4 h-4" data-id="element-1340" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg border-dashed" data-id="element-1341">
                      <div className="flex items-center justify-center py-4" data-id="element-1342">
                        <button className="flex flex-col items-center gap-2 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors" data-id="element-1343">
                          <UserPlus className="w-6 h-6" data-id="element-1344" />
                          <span className="text-sm font-medium" data-id="element-1345">
                            {isRTL ? 'دعوة عضو جديد' : 'Invite New Member'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1346">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1347">
                    {isRTL ? 'دعوات معلقة' : 'Pending Invitations'}
                  </h3>
                  <div className="space-y-4" data-id="element-1348">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1349">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-1350">
                        <div data-id="element-1351">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1352">
                            michael@example.com
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1353">
                            {isRTL ? 'تم الإرسال: 10 مايو 2025' : 'Sent: May 10, 2025'}{' '}
                            • {isRTL ? 'محرر' : 'Editor'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1354">
                          <button className="px-3 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1355">
                            {isRTL ? 'إعادة إرسال' : 'Resend'}
                          </button>
                          <button className="px-3 py-1 text-xs border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1356">
                            {isRTL ? 'إلغاء' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1357">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1358">
                    {isRTL ? 'إعدادات الفريق' : 'Team Settings'}
                  </h3>
                  <div className="space-y-6" data-id="element-1359">
                    <div data-id="element-1360">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1361">
                        {isRTL ? 'اسم الفريق' : 'Team Name'}
                      </label>
                      <input type="text" value="Arabic With English Team" className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1362" />
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1363">
                      <div data-id="element-1364">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1365">
                          {isRTL ? 'السماح بالدعوات من قبل الأعضاء' : 'Allow Member Invitations'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1366">
                          {isRTL ? 'السماح لأعضاء الفريق بدعوة أعضاء جدد' : 'Allow team members to invite new members'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-1367">
                        <input type="checkbox" className="sr-only peer" defaultChecked={true} data-id="element-1368" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" data-id="element-1369"></div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1370">
                  <div className="flex justify-end" data-id="element-1371">
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1372">
                      {isRTL ? 'حفظ إعدادات الفريق' : 'Save Team Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Help & Support Settings */}
          {activeTab === 'help' && <div data-id="element-1373">
              <div className="flex items-center justify-between mb-6" data-id="element-1374">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1375">
                  {isRTL ? 'المساعدة والدعم' : 'Help & Support'}
                </h2>
              </div>
              <div className="space-y-8" data-id="element-1376">
                <div data-id="element-1377">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1378">
                    {isRTL ? 'مركز المساعدة' : 'Help Center'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1379">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-700 transition-colors" data-id="element-1380">
                      <div className="flex items-start gap-3" data-id="element-1381">
                        <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30" data-id="element-1382">
                          <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1383" />
                        </div>
                        <div data-id="element-1384">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1385">
                            {isRTL ? 'مركز المعرفة' : 'Knowledge Base'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-1386">
                            {isRTL ? 'استكشف مقالات ودروس تعليمية مفصلة' : 'Explore detailed articles and tutorials'}
                          </p>
                          <a href="#" className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1" data-id="element-1387">
                            {isRTL ? 'استكشاف المقالات' : 'Browse Articles'}
                            <ExternalLink className="w-3 h-3" data-id="element-1388" />
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-700 transition-colors" data-id="element-1389">
                      <div className="flex items-start gap-3" data-id="element-1390">
                        <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30" data-id="element-1391">
                          <Video className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1392" />
                        </div>
                        <div data-id="element-1393">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1394">
                            {isRTL ? 'فيديوهات تعليمية' : 'Video Tutorials'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-1395">
                            {isRTL ? 'شاهد مقاطع فيديو حول كيفية استخدام المنصة' : 'Watch videos on how to use the platform'}
                          </p>
                          <a href="#" className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1" data-id="element-1396">
                            {isRTL ? 'مشاهدة الفيديوهات' : 'Watch Videos'}
                            <ExternalLink className="w-3 h-3" data-id="element-1397" />
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-700 transition-colors" data-id="element-1398">
                      <div className="flex items-start gap-3" data-id="element-1399">
                        <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30" data-id="element-1400">
                          <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1401" />
                        </div>
                        <div data-id="element-1402">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1403">
                            {isRTL ? 'الدردشة المباشرة' : 'Live Chat'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-1404">
                            {isRTL ? 'تحدث مع فريق الدعم لدينا مباشرة' : 'Chat with our support team directly'}
                          </p>
                          <a href="#" className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1" data-id="element-1405">
                            {isRTL ? 'بدء الدردشة' : 'Start Chat'}
                            <ExternalLink className="w-3 h-3" data-id="element-1406" />
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-700 transition-colors" data-id="element-1407">
                      <div className="flex items-start gap-3" data-id="element-1408">
                        <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/30" data-id="element-1409">
                          <Phone className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-1410" />
                        </div>
                        <div data-id="element-1411">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1412">
                            {isRTL ? 'دعم الهاتف' : 'Phone Support'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-1413">
                            {isRTL ? 'اتصل بنا للحصول على مساعدة مباشرة' : 'Call us for direct assistance'}
                          </p>
                          <a href="#" className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1" data-id="element-1414">
                            +1 (555) 123-4567
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1415">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1416">
                    {isRTL ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
                  </h3>
                  <div className="space-y-4" data-id="element-1417">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1418">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-1419">
                        {isRTL ? 'كيف يمكنني تغيير خطة الاشتراك الخاصة بي؟' : 'How can I change my subscription plan?'}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1420">
                        {isRTL ? 'يمكنك تغيير خطة الاشتراك الخاصة بك من خلال الانتقال إلى قسم "الفواتير" في إعدادات حسابك. هناك، ستجد خيارات لترقية أو تخفيض خطتك الحالية.' : 'You can change your subscription plan by navigating to the "Billing" section in your account settings. There, you\'ll find options to upgrade or downgrade your current plan.'}
                      </p>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1421">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-1422">
                        {isRTL ? 'كيف يمكنني إضافة أعضاء فريق جدد؟' : 'How do I add new team members?'}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1423">
                        {isRTL ? 'لإضافة أعضاء فريق جدد، انتقل إلى قسم "أعضاء الفريق" في الإعدادات وانقر على زر "دعوة عضو". ستتمكن من إدخال عنوان البريد الإلكتروني للشخص وتعيين دوره.' : 'To add new team members, go to the "Team Members" section in settings and click the "Invite Member" button. You\'ll be able to enter the person\'s email address and assign their role.'}
                      </p>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1424">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-1425">
                        {isRTL ? 'كيف يمكنني تصدير بياناتي؟' : 'How can I export my data?'}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1426">
                        {isRTL ? 'يمكنك تصدير بياناتك من خلال قسم "الخصوصية" في إعدادات حسابك. هناك، ستجد خيار "تصدير بياناتي" الذي سيسمح لك بتنزيل نسخة من جميع بياناتك الشخصية والمحتوى الخاص بك.' : 'You can export your data from the "Privacy" section in your account settings. There, you\'ll find an "Export My Data" option that will allow you to download a copy of all your personal data and content.'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 text-center" data-id="element-1427">
                    <a href="#" className="text-primary-600 dark:text-primary-400 hover:underline text-sm inline-flex items-center gap-1" data-id="element-1428">
                      {isRTL ? 'عرض جميع الأسئلة الشائعة' : 'View All FAQs'}
                      <ExternalLink className="w-3 h-3" data-id="element-1429" />
                    </a>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1430">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1431">
                    {isRTL ? 'اتصل بنا' : 'Contact Us'}
                  </h3>
                  <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1432">
                    <form className="space-y-4" data-id="element-1433">
                      <div data-id="element-1434">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1435">
                          {isRTL ? 'الموضوع' : 'Subject'}
                        </label>
                        <select className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1436">
                          <option value="" data-id="element-1437">
                            {isRTL ? 'اختر موضوعًا...' : 'Select a subject...'}
                          </option>
                          <option value="billing" data-id="element-1438">
                            {isRTL ? 'استفسار عن الفواتير' : 'Billing Inquiry'}
                          </option>
                          <option value="technical" data-id="element-1439">
                            {isRTL ? 'مشكلة فنية' : 'Technical Issue'}
                          </option>
                          <option value="account" data-id="element-1440">
                            {isRTL ? 'إدارة الحساب' : 'Account Management'}
                          </option>
                          <option value="other" data-id="element-1441">
                            {isRTL ? 'آخر' : 'Other'}
                          </option>
                        </select>
                      </div>
                      <div data-id="element-1442">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1443">
                          {isRTL ? 'الرسالة' : 'Message'}
                        </label>
                        <textarea rows={4} placeholder={isRTL ? 'اكتب رسالتك هنا...' : 'Type your message here...'} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-1444"></textarea>
                      </div>
                      <div className="flex justify-end" data-id="element-1445">
                        <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors" data-id="element-1446">
                          {isRTL ? 'إرسال الرسالة' : 'Send Message'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1447">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1448">
                    {isRTL ? 'ساعات العمل' : 'Support Hours'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1449">
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1450">
                      <div className="flex items-start gap-3" data-id="element-1451">
                        <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30" data-id="element-1452">
                          <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1453" />
                        </div>
                        <div data-id="element-1454">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1455">
                            {isRTL ? 'دعم الدردشة' : 'Chat Support'}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1456">
                            {isRTL ? 'الاثنين - الجمعة: 9:00 صباحًا - 6:00 مساءً بتوقيت جرينتش' : 'Monday - Friday: 9:00 AM - 6:00 PM GMT'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1457">
                      <div className="flex items-start gap-3" data-id="element-1458">
                        <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30" data-id="element-1459">
                          <Phone className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1460" />
                        </div>
                        <div data-id="element-1461">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-1462">
                            {isRTL ? 'دعم الهاتف' : 'Phone Support'}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1463">
                            {isRTL ? 'الاثنين - الجمعة: 10:00 صباحًا - 4:00 مساءً بتوقيت جرينتش' : 'Monday - Friday: 10:00 AM - 4:00 PM GMT'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </Card>
      </div>
    </div>;
};
// Helper components
const Badge = ({
  children,
  variant = 'primary',
  size = 'md',
  className = ''
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) => {
  const variantClasses = {
    primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400',
    secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  };
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };
  return <span className={`inline-flex items-center font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]} ${className}`} data-id="element-1464">
      {children}
    </span>;
};
// Missing Component for Settings Page
const Moon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1465">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" data-id="element-1466"></path>
  </svg>;
const Sun = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1467">
    <circle cx="12" cy="12" r="5" data-id="element-1468"></circle>
    <line x1="12" y1="1" x2="12" y2="3" data-id="element-1469"></line>
    <line x1="12" y1="21" x2="12" y2="23" data-id="element-1470"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" data-id="element-1471"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" data-id="element-1472"></line>
    <line x1="1" y1="12" x2="3" y2="12" data-id="element-1473"></line>
    <line x1="21" y1="12" x2="23" y2="12" data-id="element-1474"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" data-id="element-1475"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" data-id="element-1476"></line>
  </svg>;
const Computer = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1477">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" data-id="element-1478"></rect>
    <line x1="8" y1="21" x2="16" y2="21" data-id="element-1479"></line>
    <line x1="12" y1="17" x2="12" y2="21" data-id="element-1480"></line>
  </svg>;
const formatDateExample = (date: Date, format: string) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[date.getMonth()];
  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    case 'DD-MMM-YYYY':
      return `${day}-${monthName}-${year}`;
    default:
      return `${month}/${day}/${year}`;
  }
};
const formatTimeExample = (date: Date, format: string) => {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  return format === '24' ? `${hours24}:${minutes}` : `${hours12}:${minutes} ${ampm}`;
};
const formatNumberExample = (number: number, locale: string) => {
  try {
    return new Intl.NumberFormat(locale).format(number);
  } catch (error) {
    return number.toString();
  }
};